import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { finalize } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

import { MenuProduct } from '../../../models/menu-product.model';
import { ChannelService } from '../../../services/channels/channel.service';
import { Channel } from '../../../services/channels/channel.interface';
import { CheckboxComponent } from '../../atoms/checkbox/checkbox.component';

type DialogData = {
  product: MenuProduct;
};

@Component({
  selector: 'ui-menu-product-channels-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatSnackBarModule, CheckboxComponent],
  templateUrl: './menu-product-channels-dialog.component.html',
  styleUrls: ['./menu-product-channels-dialog.component.scss']
})
export class MenuProductChannelsDialogComponent implements OnInit {
  loading = false;
  saving = false;

  productUuid: string = '';
  productName: string = '';

  channels: Channel[] = [];

  original = new Set<string>();
  selected = new Set<string>();

  constructor(
    private readonly ref: MatDialogRef<MenuProductChannelsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly channelsSvc: ChannelService,
    private readonly snack: MatSnackBar,
  ) {
    const p: any = data?.product as any;
    this.productUuid = String(p?.uuid || p?.id || '');
    this.productName = String(p?.name || '');
  }

  ngOnInit(): void {
    this.load();
  }

  close(): void {
    this.ref.close();
  }

  private inferMembership(channel: Channel): boolean {
    const ch: any = channel as any;
    const productUuid = this.productUuid;

    const fromUuids: any[] = Array.isArray(ch?.menuProductUuids) ? ch.menuProductUuids : [];
    if (fromUuids.some((u: any) => String(u) === productUuid)) return true;

    const fromMenuProducts: any[] = Array.isArray(ch?.menuProducts) ? ch.menuProducts : [];
    if (fromMenuProducts.some((mp: any) => String(mp?.uuid || mp?.id || '') === productUuid)) return true;

    return false;
  }

  load(): void {
    if (!this.productUuid) {
      this.channels = [];
      this.original = new Set();
      this.selected = new Set();
      return;
    }

    this.loading = true;
    this.channelsSvc.listChannels().pipe(finalize(() => (this.loading = false))).subscribe({
      next: (chs) => {
        const list = (chs || []).map((c: any) => ({ ...c, uuid: String(c?.uuid || c?.id || '') } as Channel));
        this.channels = list
          .filter(c => !!c.uuid)
          .filter((c: any) => {
            const slug = String(c?.slug || '').toLowerCase();
            const name = String(c?.name || '').toLowerCase();
            return slug !== 'default' && name !== 'default';
          });

        const selected = new Set<string>();
        this.channels.forEach((c) => {
          if (this.inferMembership(c)) selected.add(String(c.uuid));
        });

        this.original = new Set(Array.from(selected));
        this.selected = new Set(Array.from(selected));
      },
      error: (err) => {
        console.error('Load channels error', err);
        this.channels = [];
        this.original = new Set();
        this.selected = new Set();
        this.snack.open('No se pudieron cargar los canales', 'Cerrar', { duration: 2500 });
      }
    });
  }

  toggle(channelUuid: string, checked: boolean): void {
    const id = String(channelUuid || '');
    if (!id) return;
    if (checked) this.selected.add(id);
    else this.selected.delete(id);
  }

  reset(): void {
    this.selected = new Set(Array.from(this.original));
  }

  hasChanges(): boolean {
    if (this.selected.size !== this.original.size) return true;
    for (const v of this.original) {
      if (!this.selected.has(v)) return true;
    }
    return false;
  }

  async save(): Promise<void> {
    if (!this.productUuid) return;

    const toAdd: string[] = [];
    const toRemove: string[] = [];

    this.selected.forEach((c) => {
      if (!this.original.has(c)) toAdd.push(c);
    });

    this.original.forEach((c) => {
      if (!this.selected.has(c)) toRemove.push(c);
    });

    if (toAdd.length === 0 && toRemove.length === 0) return;

    this.saving = true;
    try {
      for (const ch of toAdd) {
        await firstValueFrom(this.channelsSvc.linkMenuProductsToChannel(ch, [this.productUuid]));
      }
      for (const ch of toRemove) {
        await firstValueFrom(this.channelsSvc.unlinkMenuProductFromChannel(ch, this.productUuid));
      }

      this.original = new Set(Array.from(this.selected));
      this.snack.open('Canales actualizados', 'Cerrar', { duration: 2000 });
      this.ref.close(true);
    } catch (err) {
      console.error('Save channels error', err);
      this.snack.open('No se pudieron guardar los canales', 'Cerrar', { duration: 2500 });
    } finally {
      this.saving = false;
    }
  }
}
